import { useState } from "react";
import { toast } from "sonner";
import { Inbox, Paperclip, Download, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/components/Brand";
import { api, errMsg, useData } from "@/lib/api";
import { REQUEST_LABEL, fmtDateTime } from "@/lib/crm";

const Row = ({ k, v }) => (
  <div className="grid grid-cols-[120px_1fr] gap-2 text-sm"><span className="text-stone-500">{k}</span><span className="font-medium text-[#19082B] break-words">{v}</span></div>
);

function Details({ r }) {
  const d = r.details || {};
  const download = async () => {
    const res = await api.get(`/requests/${r.id}/attachment`, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(res.data);
    a.download = d.attachment_name;
    a.click();
    URL.revokeObjectURL(a.href);
  };
  if (r.key === "data_entry") return (
    <div className="space-y-1.5 rounded-xl border bg-[#FDFDFE] p-4" data-testid="request-details">
      <Row k="Name" v={`${d.first_name} ${d.last_name}`} /><Row k="Email" v={d.email} /><Row k="Phone" v={d.phone} /><Row k="Address" v={d.address} />
      <Row k="Source" v={d.source} /><Row k="Interested in" v={d.interest} /><Row k="Message" v={d.note} />
    </div>
  );
  if (r.key === "data_management") return (
    <div className="flex items-center justify-between gap-3 rounded-xl border bg-[#FDFDFE] p-4" data-testid="request-details">
      <div className="flex items-center gap-3 min-w-0"><Paperclip className="h-5 w-5 text-[#3B185F] shrink-0" />
        <div className="min-w-0"><div className="font-medium text-sm truncate">{d.attachment_name}</div><div className="text-xs text-stone-500">{d.record_count} records · CSV</div></div></div>
      <Button size="sm" variant="outline" onClick={download} className="border-[#D4AF37]" data-testid="request-download-attachment-btn"><Download className="h-4 w-4 mr-1.5" />Download</Button>
    </div>
  );
  if (r.key === "calendar") return (
    <div className="space-y-1.5 rounded-xl border bg-[#FDFDFE] p-4" data-testid="request-details">
      <Row k="Contact" v={d.contact_name} /><Row k="Requested" v={`${d.date} at ${d.time}`} /><Row k="Topic" v={d.description} />
    </div>
  );
  if (r.key === "email_drip") return (
    <div className="space-y-3 rounded-xl border bg-[#FDFDFE] p-4" data-testid="request-details">
      <Row k="Campaign" v={d.campaign_name} /><Row k="From" v={`${d.from_name} <${d.from_email}>`} /><Row k="Recipients" v={`All contacts tagged "${d.recipient_tag}"`} />
      <div className="space-y-2 pt-1">
        {d.steps.map((s) => (
          <div key={s.day} className="rounded-lg border p-3 text-sm">
            <div className="flex gap-2 items-center"><span className="rounded bg-[#3B185F] px-2 py-0.5 text-[10px] font-bold text-white">Day {s.day}</span><span className="font-semibold">{s.subject}</span></div>
            <p className="mt-1.5 whitespace-pre-wrap text-xs text-stone-600">{s.body}</p>
          </div>
        ))}
      </div>
    </div>
  );
  return null;
}

function RequestDialog({ r, onClose, onChanged }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const canSubmit = r && !["Submitted", "Completed"].includes(r.status);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/requests/${r.id}/submit`, { note });
      toast.success("Marked as done. AVY will review it shortly.");
      onChanged();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={!!r} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="request-dialog">
        {r && (
          <>
            <DialogHeader>
              <div className="flex items-center gap-2 text-xs text-stone-500"><span>From {r.sender}</span><span>·</span><span>{fmtDateTime(r.created_at)}</span></div>
              <DialogTitle className="font-display text-2xl" data-testid="request-dialog-title">{r.title}</DialogTitle>
              <DialogDescription className="sr-only">Request details</DialogDescription>
              <div><StatusBadge status={r.status} label={REQUEST_LABEL[r.status]} testId="request-dialog-status" /></div>
            </DialogHeader>
            <p className="whitespace-pre-wrap text-sm text-stone-700" data-testid="request-body">{r.body}</p>
            <Details r={r} />
            {r.status === "Needs Correction" && r.review_note && (
              <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800" data-testid="request-review-note"><span className="font-semibold">Note from AVY: </span>{r.review_note}</div>
            )}
            {r.status === "Completed" && (
              <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800" data-testid="request-completed-note"><CheckCircle2 className="h-4 w-4" />Completed{r.review_note && ` · ${r.review_note}`}</div>
            )}
            {canSubmit && (
              <div className="space-y-2 border-t pt-4">
                <Textarea rows={2} placeholder="Add a note for AVY (optional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="request-note-input" />
                <Button onClick={submit} disabled={busy} className="bg-[#3B185F]" data-testid="request-submit-btn"><CheckCircle2 className="h-4 w-4 mr-1.5" />{busy ? "Saving…" : "Mark as done"}</Button>
              </div>
            )}
            {r.status === "Submitted" && <p className="text-xs text-stone-500 border-t pt-4">Submitted {fmtDateTime(r.submitted_at)} · awaiting review from AVY.</p>}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function RequestsInbox() {
  const [items, reload] = useData("/requests", []);
  const [openId, setOpenId] = useState(null);
  const open = items.filter((r) => !["Completed"].includes(r.status)).length;
  return (
    <section className="space-y-4" data-testid="requests-inbox">
      <div className="flex items-end justify-between">
        <div>
          <h2 className="text-lg font-bold text-[#19082B] flex items-center gap-2"><Inbox className="h-5 w-5 text-[#3B185F]" />Requests</h2>
          <p className="text-sm text-stone-500">{open ? `${open} open request${open === 1 ? "" : "s"} need your attention.` : "You're all caught up."}</p>
        </div>
      </div>
      <div className="rounded-2xl border bg-white divide-y overflow-hidden">
        {items.map((r) => (
          <button key={r.id} type="button" onClick={() => setOpenId(r.id)} className="w-full text-left flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 px-5 py-4 hover:bg-[#FFFDF0] transition-colors" data-testid={`request-row-${r.key}`}>
            <div className="sm:w-56 shrink-0 text-xs text-stone-500 truncate">{r.sender.replace(/<.*>/, "").trim()}</div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-[#19082B] truncate">{r.title}</div>
              <div className="text-xs text-stone-500 truncate">{r.body}</div>
            </div>
            <StatusBadge status={r.status} label={REQUEST_LABEL[r.status]} testId={`request-status-${r.key}`} />
          </button>
        ))}
        {!items.length && <div className="p-10 text-center text-sm text-stone-500" data-testid="requests-empty">No requests right now.</div>}
      </div>
      <RequestDialog r={items.find((r) => r.id === openId)} onClose={() => setOpenId(null)} onChanged={reload} />
    </section>
  );
}
